/**
 * Admin API client. All routes are guarded by JWT + RBAC on the backend;
 * we simply hit them with the shared `api` axios instance (cookies attach
 * automatically because `withCredentials: true` is set).
 */

import { api } from "@/features/auth/api";

export type AdminRole = "super_admin" | "operations_admin" | "store_admin" | "staff";

export type AdminPermissions = {
    manage_products: boolean;
    manage_categories: boolean;
    manage_orders: boolean;
    refund_orders: boolean;
    manage_customers: boolean;
    manage_coupons: boolean;
    manage_notifications: boolean;
    manage_stores: boolean;
    manage_team: boolean;
    view_activity: boolean;
};

export type AdminSession = {
    user: { id: string; email: string; full_name: string; role: string };
    role: AdminRole;
    permissions: AdminPermissions;
};

export type AdminStats = {
    counts: {
        orders: number;
        active_orders: number;
        customers: number;
        products: number;
        low_stock: number;
        out_of_stock: number;
    };
    revenue: {
        total: number;
        today: number;
        today_orders: number;
        weekly: number;
        weekly_orders: number;
    };
    series: Array<{ date: string; revenue: number; orders: number }>;
    best_sellers: Array<{ product_id: string; name: string; quantity: number; revenue: number }>;
    recent_orders: Array<{
        id: string;
        number: string;
        total: number;
        status: string;
        placed_at: string;
        user_id: string;
    }>;
    generated_at: string;
};

export type ProductVariantInput = {
    label: string;
    weight: string;
    unit: string;
    price: number;
    mrp: number;
};

export type AdminProduct = {
    id: string;
    slug: string;
    name: string;
    brand: string;
    category_slug: string;
    category_name: string;
    price: number;
    mrp: number;
    weight: string;
    unit: string;
    stock_count: number;
    low_stock_threshold: number;
    in_stock: boolean;
    tag: string | null;
    glyph: string;
    tone: string[];
    eta_minutes: number;
    description: string;
    ingredients: string[];
    images: string[];
    variants: ProductVariantInput[];
    is_active: boolean;
    show_picture: boolean;
    rating: number;
    rating_count: number;
};

export type AdminCategory = {
    id: string;
    slug: string;
    name: string;
    subtitle: string;
    description: string;
    icon: string;
    gradient: string;
    accent: string;
    banner_gradient: string;
    order: number;
    is_active: boolean;
};

export type AdminCoupon = {
    id: string;
    code: string;
    title: string;
    description: string;
    kind: "flat" | "percent" | "free_delivery";
    value: number;
    min_order: number;
    max_discount: number;
    usage_limit: number | null;
    used_count: number;
    expires_at: string | null;
    is_active: boolean;
};

export type AdminOrder = {
    id: string;
    number: string;
    status: string;
    total: number;
    item_count: number;
    payment: { method: string; status: string };
    placed_at: string;
    customer: {
        id: string;
        email: string;
        full_name: string;
        phone: string;
    } | null;
    // Only present when fetching a single order
    items?: Array<{
        product_id: string;
        name: string;
        quantity: number;
        price: number;
        mrp: number;
        weight: string;
    }>;
    breakdown?: Record<string, number>;
    address?: Record<string, string>;
    delivery_slot?: string;
    estimated_delivery_at?: string;
    cancellation_reason?: string | null;
    scoped_to_store?: string | null;
    store_groups?: Array<{
        store_id: string | null;
        store_name: string;
        item_count: number;
        subtotal: number;
        status: string;
    }>;
};

export type AdminCustomer = {
    id: string;
    email: string;
    full_name: string;
    phone: string;
    role: string;
    is_blocked: boolean;
    order_count: number;
    created_at: string | null;
};

export type AdminBroadcast = {
    id: string;
    subject: string;
    body: string;
    audience: "all" | "active" | "blocked";
    recipient_count: number;
    sent_at: string;
};

// ---------- Endpoints ----------

export const adminApi = {
    session: async (): Promise<AdminSession> => (await api.get("/admin/session")).data,

    stats: async (): Promise<AdminStats> => (await api.get("/admin/stats")).data,

    listProducts: async (
        params: { search?: string; category?: string; stock?: string; page?: number; page_size?: number } = {},
    ): Promise<{ items: AdminProduct[]; total: number; page: number; page_size: number }> =>
        (await api.get("/admin/products", { params })).data,

    getProduct: async (id: string): Promise<AdminProduct> =>
        (await api.get(`/admin/products/${id}`)).data,

    createProduct: async (payload: Partial<AdminProduct>): Promise<AdminProduct> =>
        (await api.post("/admin/products", payload)).data,

    updateProduct: async (id: string, payload: Partial<AdminProduct>): Promise<AdminProduct> =>
        (await api.put(`/admin/products/${id}`, payload)).data,

    deleteProduct: async (id: string): Promise<void> => {
        await api.delete(`/admin/products/${id}`);
    },

    duplicateProduct: async (id: string): Promise<AdminProduct> =>
        (await api.post(`/admin/products/${id}/duplicate`)).data,

    bulkProducts: async (products: Array<Record<string, unknown>>): Promise<{ created: number; errors: unknown[] }> =>
        (await api.post("/admin/products/bulk", { products })).data,

    uploadImage: async (file: File, purpose = "products"): Promise<{ url: string; storage_path: string; size: number }> => {
        const form = new FormData();
        form.append("file", file);
        form.append("purpose", purpose);
        const { data } = await api.post("/admin/uploads/image", form, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return data;
    },

    listCategories: async (): Promise<AdminCategory[]> =>
        (await api.get("/admin/categories")).data,

    createCategory: async (payload: Partial<AdminCategory>): Promise<AdminCategory> =>
        (await api.post("/admin/categories", payload)).data,

    updateCategory: async (id: string, payload: Partial<AdminCategory>): Promise<AdminCategory> =>
        (await api.put(`/admin/categories/${id}`, payload)).data,

    deleteCategory: async (id: string): Promise<void> => {
        await api.delete(`/admin/categories/${id}`);
    },

    listOrders: async (
        params: { status?: string; search?: string; page?: number; page_size?: number } = {},
    ): Promise<{ items: AdminOrder[]; total: number; page: number; page_size: number }> =>
        (await api.get("/admin/orders", { params })).data,

    getOrder: async (id: string): Promise<AdminOrder> =>
        (await api.get(`/admin/orders/${id}`)).data,

    updateOrderStatus: async (id: string, status: string, reason?: string): Promise<AdminOrder> =>
        (await api.patch(`/admin/orders/${id}/status`, { status, reason })).data,
    updateStoreGroupStatus: async (
        orderId: string,
        storeId: string,
        status: "pending" | "accepted" | "preparing" | "ready",
    ): Promise<AdminOrder> =>
        (await api.patch(`/admin/orders/${orderId}/groups/${storeId}/status`, { status })).data,

    refundOrder: async (id: string): Promise<AdminOrder> =>
        (await api.post(`/admin/orders/${id}/refund`)).data,

    listCustomers: async (
        params: { search?: string; blocked?: boolean; page?: number; page_size?: number } = {},
    ): Promise<{ items: AdminCustomer[]; total: number; page: number; page_size: number }> =>
        (await api.get("/admin/customers", { params })).data,

    blockCustomer: async (id: string, is_blocked: boolean): Promise<AdminCustomer> =>
        (await api.patch(`/admin/customers/${id}/block`, { is_blocked })).data,

    customerOrders: async (id: string): Promise<AdminOrder[]> =>
        (await api.get(`/admin/customers/${id}/orders`)).data,

    listCoupons: async (): Promise<AdminCoupon[]> =>
        (await api.get("/admin/coupons")).data,

    createCoupon: async (payload: Partial<AdminCoupon>): Promise<AdminCoupon> =>
        (await api.post("/admin/coupons", payload)).data,

    updateCoupon: async (id: string, payload: Partial<AdminCoupon>): Promise<AdminCoupon> =>
        (await api.put(`/admin/coupons/${id}`, payload)).data,

    deleteCoupon: async (id: string): Promise<void> => {
        await api.delete(`/admin/coupons/${id}`);
    },

    listBroadcasts: async (): Promise<AdminBroadcast[]> =>
        (await api.get("/admin/notifications")).data,

    broadcast: async (payload: { subject: string; body: string; audience: string }): Promise<{ sent: number; audience: string }> =>
        (await api.post("/admin/notifications/broadcast", payload)).data,
};
